import axios from 'axios';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollToPlugin);

export default class Validation {
  constructor(formEl, hasRecaptcha = false) {
    this.dom = {
      formEl,
      fields: [],
      message: formEl.querySelector('.js-message'),
    };

    this.states = {
      idle: 'STATE_IDLE',
      awaitingRequest: 'STATE_AWAITING_REQUEST',
    };

    this.props = {
      url: formEl.getAttribute('action') || '',
      hasRecaptcha,
    };

    this.data = {
      invalidClass: 'is-invalid',
      message: {
        text: null,
        class: null,
      },
      formValidationClass: 'was-validated',
      state: this.states.idle,
      errors: null,
    };

    this.events = {
      onContactSubmit: this.onContactSubmit.bind(this),
      submitRecaptcha: this.submitRecaptcha.bind(this),
      onValidatedInputChange: this.onValidatedInputChange.bind(this),
      onRequestSuccess: this.onRequestSuccess.bind(this),
      onRequestError: this.onRequestError.bind(this),
      dispatchSubmit: null,
    };

    this.mount();
  }

  mount() {
    if (this.dom.formEl) {
      if (this.props.hasRecaptcha) {
        this.dom.formEl.addEventListener('submit', this.events.submitRecaptcha);
      } else {
        this.dom.formEl.addEventListener('submit', this.events.onContactSubmit);
      }
    }
  }

  submitRecaptcha(e) {
    e.preventDefault();

    window.grecaptcha.ready(() => {
      window.grecaptcha.execute('6LeL9k4gAAAAACdpt62Opap0WOuhPkWH0Bd_rzai', { action: 'submit_contact_form' }).then((token) => {
        const recaptchaAction = this.dom.formEl.querySelector('input[name="recaptcha-action"]');
        const recaptchaToken = this.dom.formEl.querySelector('input[name="recaptcha-token"]');

        if (recaptchaAction) recaptchaAction.remove();
        if (recaptchaToken) recaptchaToken.remove();

        // create token hidden input
        const inputToken = document.createElement('input');
        inputToken.type = 'hidden';
        inputToken.name = 'recaptcha-token';
        inputToken.value = token;

        // create action hidden input
        const inputAction = document.createElement('input');
        inputAction.type = 'hidden';
        inputAction.name = 'recaptcha-action';
        inputAction.value = 'submit_contact_form';

        // prepend to form
        this.dom.formEl.prepend(inputToken);
        this.dom.formEl.prepend(inputAction);

        this.events.onContactSubmit(e);
      });
    });
  }

  /**
   * form has been submitted
   * @param e {Event}
   */
  onContactSubmit(e) {
    // stay on page
    e.preventDefault();

    // awaiting server response, ignore click
    if (this.data.state === this.states.awaitingRequest) return;

    // reset message state
    this.resetMessage();

    // set fields
    this.dom.fields = [...this.dom.formEl.querySelectorAll('[name]:not([type="hidden"])')];

    // disallow form submit
    this.data.state = this.states.awaitingRequest;

    // set formData
    const formData = new FormData(this.dom.formEl);

    // send request
    const promise = axios.post(this.props.url, formData)
      .then(this.events.onRequestSuccess)
      .catch(this.events.onRequestError);

    this.events.dispatchSubmit = new CustomEvent('onsubmit', { detail: { promise } });
    this.dom.formEl.dispatchEvent(this.events.dispatchSubmit);
  }

  /**
   * request was successful
   * @param response {Object}
   */
  onRequestSuccess(response) {
    this.data.errors = response.data.errors || [];
    this.parseMessage(response.data.message);
    // this.setMessage(); // uncomment to always show message (error or success)

    // unbind change events
    this.dom.fields.forEach((f) => f.removeEventListener('change', this.events.onValidatedInputChange));

    if (this.data.errors && Object.keys(this.data.errors).length > 0) {
      // form has errors
      this.handleErrors();
    } else {
      this.setMessage();
      // form was submitted successfully
      this.handleSuccess();
    }

    // allow form submit
    this.data.state = this.states.idle;
  }

  /**
   * request has failed
   * @param error {Object}
   */
  onRequestError(error) {
    console.error(error);

    // allow form submit
    this.data.state = this.states.idle;
  }

  // parse message object
  parseMessage(messageObj) {
    switch (messageObj.type) {
      case 'error':
        this.data.message.class = 'alert-danger';
        break;
      case 'success':
        this.data.message.class = 'alert-success';
        break;
      default:
        this.data.message.class = null;
        break;
    }

    // set text
    if (messageObj.text) {
      this.data.message.text = messageObj.text;
    } else {
      this.data.message.text = null;
    }
  }

  // show message
  setMessage() {
    // if we have no message, exit fn
    if (!this.data.message.text) return;

    // apply classes
    if (this.data.message.class) {
      this.dom.message.classList.add(this.data.message.class);
    }

    // apply text
    this.dom.message.textContent = this.data.message.text;

    // show message
    this.dom.message.classList.remove('d-none');

    // scroll to message
    gsap.to(window, { duration: 0.35, scrollTo: this.dom.message, ease: 'power2.out' });
  }

  // empty message and hide it
  resetMessage() {
    // reset class
    this.dom.message.classList.add('d-none');
    if (this.data.message.class) {
      this.dom.message.classList.remove(this.data.message.class);
    }
    // reset text
    this.dom.message.textContent = '';
  }

  /**
   * validated input has changed
   * @param e {Event}
   */
  onValidatedInputChange(e) {
    const field = e.target;

    // exception for selects
    if (field.parentNode.classList.contains('select-outer')) {
      field.parentNode.classList.remove(this.data.invalidClass);
    } else {
      field.classList.remove(this.data.invalidClass);
    }

    // unbind change event
    field.removeEventListener('change', this.events.onValidatedInputChange);
  }

  // form was submitted successfully
  handleSuccess() {
    // hide validation states
    this.dom.formEl.classList.remove(this.data.formValidationClass);

    // reset form
    this.dom.formEl.reset();
  }

  // form has errors
  handleErrors() {
    for (const [key, value] of Object.entries(this.data.errors)) {
      // get element that has an error
      const field = this.dom.fields.find((f) => f.name === key);

      if (field) {
        const selectWrapper = field.closest('.select-outer');
        let invalidFeedback = field.parentNode.querySelector('.invalid-feedback');

        // exception for selects (has additional elements required for styling)
        if (selectWrapper) {
          invalidFeedback = selectWrapper.parentNode.querySelector('.invalid-feedback');
          selectWrapper.classList.add(this.data.invalidClass);
        } else {
          // set error class
          field.classList.add(this.data.invalidClass);
        }

        // set error
        if (invalidFeedback) {
          invalidFeedback.innerHTML = value.toString();
        }

        // bind change event
        field.addEventListener('change', this.events.onValidatedInputChange);
      }
    }

    // show errors
    this.dom.formEl.classList.add(this.data.formValidationClass);
  }
}
